import React, { useEffect } from "react";
import { createPortal } from "react-dom";
// =======================================================
// AppointmentImageViewer
// -------------------------------------------------------
// Displays appointment reference images in a fullscreen
// overlay.
//
// Responsibilities:
// - Display the currently selected image
// - Navigate through multiple images
// - Support keyboard navigation
// - Close the image viewer
// - Trigger image sharing
//
// Image state remains inside Home.js.
// =======================================================

const AppointmentImageViewer = ({
  images,
  currentImage,
  setCurrentImage,
  onClose,
  onShare,
}) => {
  const hasImages = Array.isArray(images) && images.length > 0;
  const hasMultipleImages = images?.length > 1;

  // =======================================================
  // Keyboard Navigation
  // -------------------------------------------------------
  // Supports Escape, ArrowLeft and ArrowRight while the
  // fullscreen image viewer is open.
  // =======================================================

  useEffect(() => {
    if (!hasImages) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key === "ArrowRight") {
        setCurrentImage((previousIndex) =>
          previousIndex >= images.length - 1
            ? 0
            : previousIndex + 1
        );
      }

      if (event.key === "ArrowLeft") {
        setCurrentImage((previousIndex) =>
          previousIndex <= 0
            ? images.length - 1
            : previousIndex - 1
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    hasImages,
    images.length,
    onClose,
    setCurrentImage,
  ]);

  if (!hasImages) {
    return null;
  }

  const safeCurrentImage =
    currentImage >= 0 && currentImage < images.length
      ? currentImage
      : 0;

  const activeImage = images[safeCurrentImage];

  const showPreviousImage = () => {
    setCurrentImage((previousIndex) =>
      previousIndex <= 0
        ? images.length - 1
        : previousIndex - 1
    );
  };

  const showNextImage = () => {
    setCurrentImage((previousIndex) =>
      previousIndex >= images.length - 1
        ? 0
        : previousIndex + 1
    );
  };

  return createPortal(
    <div
      className="appointment-image-viewer"
      onClick={onClose}
    >
      <button
        type="button"
        className="appointment-image-viewer-close"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        title="Bild schließen"
        aria-label="Bild schließen"
      >
        ×
      </button>

      <button
        type="button"
        className="appointment-image-share"
        onClick={(event) => {
          event.stopPropagation();
          onShare(activeImage);
        }}
        title="Bild teilen"
      >
        Teilen
      </button>

      {hasMultipleImages && (
        <button
          type="button"
          className="appointment-image-nav appointment-image-nav-left"
          onClick={(event) => {
            event.stopPropagation();
            showPreviousImage();
          }}
          title="Vorheriges Bild"
          aria-label="Vorheriges Bild"
        >
          ‹
        </button>
      )}

      <img
        src={activeImage}
        alt={`Termin-Referenz ${safeCurrentImage + 1}`}
        onClick={(event) => event.stopPropagation()}
      />

      {hasMultipleImages && (
        <button
          type="button"
          className="appointment-image-nav appointment-image-nav-right"
          onClick={(event) => {
            event.stopPropagation();
            showNextImage();
          }}
          title="Nächstes Bild"
          aria-label="Nächstes Bild"
        >
          ›
        </button>
      )}

           {hasMultipleImages && (
        <div
          className="appointment-image-counter"
          onClick={(event) => event.stopPropagation()}
        >
          {safeCurrentImage + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body
  );
};

export default AppointmentImageViewer;